import { FormEvent, useEffect, useMemo, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { fmtDate } from '../lib/dates';
import { useAuth } from '../lib/auth';
import { StatCard } from '../components/Icon';

interface Release {
  id: string; version: string; channel: string; changelog?: string;
  zip_filename?: string; min_wp_version?: string; created_at: string;
}
const CHANNELS = ['test', 'beta', 'public'];

export default function Releases() {
  const { can } = useAuth();
  const canWrite = can('releases', 'write');
  const [releases, setReleases] = useState<Release[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      // Each site's release channel is shown and changed on Sites & Sandboxes.
      const r = await backendApi.get<{ releases: Release[] }>('/license/admin/releases');
      setReleases(r.releases || []); setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to releases.' : err.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  const latest = useMemo(() => {
    const pub = releases.filter((r) => r.channel === 'public');
    return (pub[0] || releases[0])?.version ?? '—';
  }, [releases]);

  async function promote(rel: Release, channel: string) {
    setError('');
    try { await backendApi.put(`/license/admin/releases/${rel.id}/promote`, { channel }); await load(); }
    catch (err: any) { setError(err.message); }
  }
  async function remove(rel: Release) {
    if (!confirm(`Delete release v${rel.version}?`)) return;
    try { await backendApi.delete(`/license/admin/releases/${rel.id}`); await load(); }
    catch (err: any) { setError(err.message); }
  }

  return (
    <>
      <header className="page-head"><div><h1>Plugin Updates</h1><p className="muted">Plugin releases and their channels. Each site's channel is set on Sites &amp; Sandboxes.</p></div></header>

      <div className="stat-row">
        <StatCard icon="download" tint="purple" value={latest === '—' ? '—' : `v${latest}`} label="Latest Version" />
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {canWrite && <PublishRelease onDone={load} />}

      {loading ? <p className="muted">Loading…</p> : (
        <>
          <div className="card">
            <h2 className="card-title">Releases</h2>
            {releases.length === 0 ? <p className="muted">No releases yet.</p> : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Version</th><th>Channel</th><th>ZIP File</th><th>Min WP</th><th>Published</th><th>Changelog</th>{canWrite && <th>Actions</th>}</tr></thead>
                  <tbody>
                    {releases.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">v{r.version}</td>
                        <td><span className={'badge ' + (r.channel === 'public' ? 'badge-green' : r.channel === 'beta' ? 'badge-amber' : 'badge-gray')}>{r.channel}</span></td>
                        <td className="mono small">{r.zip_filename || '—'}</td>
                        <td className="mono">{r.min_wp_version || '—'}</td>
                        <td className="muted">{fmtDate(r.created_at)}</td>
                        <td className="muted small">
                          {r.changelog
                            ? <div className="changelog-cell" title={r.changelog}>{r.changelog}</div>
                            : '—'}
                        </td>
                        {canWrite && (
                          <td>
                            <div className="row-actions">
                              <select defaultValue="" onChange={(e) => e.target.value && promote(r, e.target.value)}>
                                <option value="">Promote…</option>
                                {CHANNELS.filter((c) => c !== r.channel).map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                              <button className="btn btn-sm btn-danger" onClick={() => remove(r)}>Delete</button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </>
      )}
    </>
  );
}

function PublishRelease({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ version: '', zipFilename: '', channel: 'test', changelog: '', minWpVersion: '6.0' });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      await backendApi.post('/license/admin/releases', {
        version: f.version.trim(), zipFilename: f.zipFilename.trim(),
        channel: f.channel, changelog: f.changelog.trim() || undefined,
        minWpVersion: f.minWpVersion.trim() || '6.0',
      });
      setF({ version: '', zipFilename: '', channel: 'test', changelog: '', minWpVersion: '6.0' });
      onDone();
    } catch (err: any) { setError(err.body?.detail || err.body?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">Publish Release</h2>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-row">
        <label className="field"><span>Version *</span>
          <input required placeholder="e.g., 2.0.35" value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} /></label>
        <label className="field"><span>ZIP Filename *</span>
          <input required placeholder="e.g., wpcursor-ewb-v2.0.35.zip" value={f.zipFilename} onChange={(e) => setF({ ...f, zipFilename: e.target.value })} /></label>
        <label className="field"><span>Channel</span>
          <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })}>
            {CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></label>
        <label className="field"><span>Min WP Version</span>
          <input value={f.minWpVersion} onChange={(e) => setF({ ...f, minWpVersion: e.target.value })} /></label>
      </div>
      <label className="field"><span>Changelog</span>
        <textarea className="textarea" placeholder="What's new in this version…" value={f.changelog} onChange={(e) => setF({ ...f, changelog: e.target.value })} /></label>
      <button className="btn btn-primary" disabled={busy}>{busy ? 'Publishing…' : 'Publish Release'}</button>
    </form>
  );
}
